import { ArrowLeft, Edit, Loader2, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { PartPicker } from '@/components/ui/part-picker';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useCreatePartEquivalence,
  useDeletePartEquivalence,
  usePartEquivalences,
} from '@/features/part-equivalences/queries';
import {
  useDeletePart,
  usePart,
  useParts,
} from '@/features/parts/queries';
import { isGlobalPart, type Part } from '@/features/parts/types';
import { usePartType } from '@/features/part-types/queries';
import { ApiError } from '@/lib/api';
import { PartDocumentsCard } from './PartDocumentsCard';
import { PartPhotoCard } from './PartPhotoCard';
import { PartSuppliersCard } from './PartSuppliersCard';
import { cn } from '@/lib/utils';

function Field({
  label,
  value,
  fullWidth = false,
}: {
  label: string;
  value: React.ReactNode;
  fullWidth?: boolean;
}) {
  const display =
    value === null || value === undefined || value === '' ? '—' : value;
  return (
    <div className={cn(fullWidth && 'sm:col-span-2')}>
      <dt className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm">{display}</dd>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <dl className="grid gap-4 sm:grid-cols-2">{children}</dl>
      </CardContent>
    </Card>
  );
}

/**
 * AddEquivalenceDialog — modal for declaring a new equivalence
 * between the current part and another. Renders a PartPicker
 * filtered to the same partTypeId (you typically only declare
 * equivalences within the same type) and an optional notes field.
 *
 * The current part is excluded from the picker so users cannot
 * accidentally declare "A is equivalent to A".
 */
function AddEquivalenceDialog({
  open,
  onOpenChange,
  currentPart,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPart: Part;
}) {
  const { t } = useTranslation();
  const createMut = useCreatePartEquivalence();
  const [targetId, setTargetId] = useState<string | null>(null);
  const [notes, setNotes] = useState('');

  const errorMessage = (() => {
    if (!createMut.error) return null;
    if (createMut.error instanceof ApiError) {
      return t(createMut.error.key, {
        defaultValue: t('part.equivalences.saveError'),
        ...(createMut.error.params ?? {}),
      });
    }
    return t('part.equivalences.saveError');
  })();

  const handleConfirm = () => {
    if (!targetId) return;
    createMut.mutate(
      {
        partXId: currentPart.id,
        partYId: targetId,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          setTargetId(null);
          setNotes('');
          createMut.reset();
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          setTargetId(null);
          setNotes('');
          createMut.reset();
        }
        onOpenChange(o);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('part.equivalences.addTitle')}</DialogTitle>
          <DialogDescription>
            {t('part.equivalences.addDescription')}
          </DialogDescription>
        </DialogHeader>

        {errorMessage && (
          <Alert variant="destructive">
            <AlertTitle>{t('part.equivalences.saveError')}</AlertTitle>
            <AlertDescription>{errorMessage}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-3">
          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="eq-target">
              {t('part.equivalences.targetLabel')}
            </label>
            <PartPicker
              value={targetId}
              onChange={setTargetId}
              partTypeId={currentPart.partTypeId}
              excludePartId={currentPart.id}
              allowEmpty
              emptyLabel={t('part.equivalences.targetPlaceholder')}
            />
            <p className="text-xs text-muted-foreground">
              {t('part.equivalences.targetHint')}
            </p>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium" htmlFor="eq-notes">
              {t('part.equivalences.notesLabel')}
            </label>
            <Input
              id="eq-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('part.equivalences.notesPlaceholder')}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={createMut.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!targetId || createMut.isPending}
          >
            {createMut.isPending && (
              <Loader2 className="animate-spin" aria-hidden />
            )}
            {t('part.equivalences.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * PartEquivalencesCard — lists all parts equivalent to the current
 * one (X↔Y is symmetric, the backend returns either side; we
 * compute "the OTHER part" in each row by comparing IDs).
 *
 * Each row links to the other part's detail page, shows the notes
 * inline (italic, muted), and exposes a Delete button gated
 * behind a ConfirmDialog. Empty state encourages declaring the
 * first equivalence.
 */
function PartEquivalencesCard({ currentPart }: { currentPart: Part }) {
  const { t } = useTranslation();
  const { data: equivalences, isLoading } = usePartEquivalences(currentPart.id);
  const { data: allParts } = useParts();
  const deleteMut = useDeletePartEquivalence();
  const [addOpen, setAddOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<{
    id: string;
    partXId: string;
    partYId: string;
  } | null>(null);

  // Build a Map of partId → Part for O(1) lookup of the "other side"
  // of each equivalence pair.
  const partsMap = useMemo(() => {
    const m = new Map<string, Part>();
    (allParts ?? []).forEach((p) => m.set(p.id, p));
    return m;
  }, [allParts]);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">
            {t('part.equivalences.title')}
          </CardTitle>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAddOpen(true)}
          >
            <Plus aria-hidden />
            {t('part.equivalences.addButton')}
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (equivalences ?? []).length === 0 ? (
            <div className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">
              {t('part.equivalences.empty')}
            </div>
          ) : (
            <ul className="space-y-2">
              {(equivalences ?? []).map((eq) => {
                const otherId =
                  eq.partXId === currentPart.id ? eq.partYId : eq.partXId;
                const otherPart = partsMap.get(otherId);
                return (
                  <li
                    key={eq.id}
                    className="flex flex-wrap items-start justify-between gap-3 rounded-md border p-3 text-sm"
                  >
                    <div className="min-w-0 flex-1 space-y-1">
                      {otherPart ? (
                        <Link
                          to={`/parts/${otherPart.id}`}
                          className="font-medium text-primary hover:underline"
                        >
                          <span className="font-mono">{otherPart.code}</span>
                          {' — '}
                          {otherPart.name}
                        </Link>
                      ) : (
                        <span className="font-mono text-muted-foreground">
                          {otherId}
                        </span>
                      )}
                      {eq.notes && (
                        <p className="italic text-muted-foreground">
                          {eq.notes}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() =>
                        setPendingDelete({
                          id: eq.id,
                          partXId: eq.partXId,
                          partYId: eq.partYId,
                        })
                      }
                      disabled={deleteMut.isPending}
                    >
                      <Trash2 aria-hidden />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <AddEquivalenceDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        currentPart={currentPart}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setPendingDelete(null);
        }}
        title={t('part.equivalences.deleteTitle')}
        description={t('part.equivalences.deleteDescription')}
        confirmLabel={t('common.delete')}
        variant="destructive"
        loading={deleteMut.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteMut.mutate(pendingDelete, {
            onSuccess: () => setPendingDelete(null),
          });
        }}
      />
    </>
  );
}

function DetailContent({ part }: { part: Part }) {
  const { t } = useTranslation();
  const { data: partType } = usePartType(part.partTypeId);
  const effectiveUnit = part.unit ?? partType?.defaultUnit ?? null;

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Section title={t('part.detail.sectionIdentity')}>
        <Field
          label={t('part.fields.code')}
          value={<span className="font-mono">{part.code}</span>}
        />
        <Field label={t('part.fields.name')} value={part.name} />
        <Field
          label={t('part.fields.partTypeId')}
          value={
            partType ? (
              <Link
                to={`/part-types/${partType.id}`}
                className="text-primary hover:underline"
              >
                <span className="font-mono">{partType.code}</span>
                {' — '}
                {partType.name}
              </Link>
            ) : (
              part.partTypeId
            )
          }
        />
        <Field
          label={t('part.fields.unit')}
          value={
            <span className="font-mono">
              {effectiveUnit}
              {part.unit === null && partType && (
                <span className="ml-2 text-xs text-muted-foreground">
                  ({t('part.detail.inheritedFromType')})
                </span>
              )}
            </span>
          }
        />
        <Field
          label={t('part.fields.notes')}
          value={
            part.notes ? (
              <p className="whitespace-pre-line">{part.notes}</p>
            ) : null
          }
          fullWidth
        />
      </Section>

      <Section title={t('part.detail.sectionManufacturer')}>
        <Field
          label={t('part.fields.manufacturer')}
          value={part.manufacturer}
        />
        <Field
          label={t('part.fields.manufacturerPartNumber')}
          value={
            part.manufacturerPartNumber ? (
              <span className="font-mono">
                {part.manufacturerPartNumber}
              </span>
            ) : null
          }
        />
      </Section>

      <Section title={t('part.detail.sectionStock')}>
        <Field
          label={t('part.fields.minStockGlobal')}
          value={
            part.minStockGlobal === null
              ? t('part.detail.noThreshold')
              : part.minStockGlobal
          }
        />
        <Field
          label={t('part.fields.maxStockGlobal')}
          value={
            part.maxStockGlobal === null
              ? t('part.detail.noThreshold')
              : part.maxStockGlobal
          }
        />
      </Section>

      <Section title={t('part.detail.sectionScope')}>
        <Field
          label={t('part.fields.ownerCompanyId')}
          value={
            <span
              className={cn(
                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                isGlobalPart(part)
                  ? 'bg-indigo-100 text-indigo-700'
                  : 'bg-amber-100 text-amber-800',
              )}
            >
              {t(`part.scope.${isGlobalPart(part) ? 'global' : 'company'}`)}
            </span>
          }
        />
        <Field
          label={t('part.fields.isActive')}
          value={
            <span
              className={cn(
                'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
                part.isActive
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {t(`part.status.${part.isActive ? 'active' : 'inactive'}`)}
            </span>
          }
        />
      </Section>
    </div>
  );
}

export function PartDetailPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: part, isLoading, isError, error, refetch } = usePart(id);
  const deleteMut = useDeletePart();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const errorMessage = (() => {
    if (!isError) return null;
    if (error instanceof ApiError) {
      return t(error.key, {
        defaultValue: t('part.detail.loadError'),
        ...(error.params ?? {}),
      });
    }
    return t('part.detail.loadError');
  })();

  const onConfirmDelete = () => {
    if (!id) return;
    deleteMut.mutate(id, {
      onSuccess: () => {
        setConfirmOpen(false);
        navigate('/parts');
      },
    });
  };

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button
            asChild
            variant="ghost"
            size="icon"
            aria-label={t('common.back')}
          >
            <Link to="/parts">
              <ArrowLeft aria-hidden />
            </Link>
          </Button>
          <h1 className="text-2xl font-semibold">
            {part ? part.name : t('common.loading')}
          </h1>
        </div>
        {part && (
          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to={`/parts/${part.id}/edit`}>
                <Edit aria-hidden />
                {t('common.edit')}
              </Link>
            </Button>
            <Button
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              disabled={deleteMut.isPending}
            >
              {deleteMut.isPending ? (
                <Loader2 className="animate-spin" aria-hidden />
              ) : (
                <Trash2 aria-hidden />
              )}
              {t('common.delete')}
            </Button>
          </div>
        )}
      </header>

      {errorMessage && (
        <Alert variant="destructive">
          <AlertTitle>{t('part.detail.loadError')}</AlertTitle>
          <AlertDescription className="space-y-2">
            <p>{errorMessage}</p>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              {t('common.retry')}
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )}

      {part && (
        <>
          <div className="grid gap-4 lg:grid-cols-[280px_1fr]">
            <PartPhotoCard partId={part.id} photoKey={part.photoKey} />
            <DetailContent part={part} />
          </div>
          <PartDocumentsCard partId={part.id} />
          <PartSuppliersCard partId={part.id} />
          <PartEquivalencesCard currentPart={part} />
        </>
      )}

      {part && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={setConfirmOpen}
          title={t('part.delete.title')}
          description={t('part.delete.description', { name: part.name })}
          confirmLabel={t('part.delete.confirm')}
          variant="destructive"
          loading={deleteMut.isPending}
          onConfirm={onConfirmDelete}
        />
      )}
    </div>
  );
}
